import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { ResultadoDevolucion } from '@/lib/supabase/tipos-bd'
import { useDevolverPorQr } from '@/modules/programacion/useHerramientas'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'

import estilos from './PaginaDevolver.module.css'

/**
 * Devolver una herramienta.
 *
 * Aquí se llega APUNTANDO LA CÁMARA al QR de la herramienta, no navegando: el
 * QR lleva esta dirección, el teléfono la ofrece de un toque y esta pantalla
 * abre con la herramienta ya identificada. Por eso no hay ningún selector ni
 * ningún botón de escanear — no habría a quién enseñárselo.
 *
 * LO PRIMERO QUE HACE ES PEDIR LA UBICACIÓN, antes de que nadie toque nada.
 * La herramienta se recibe en la bodega y eso lo comprueba la base; pedirla
 * después de pulsar un botón haría esperar dos veces.
 */
export function PaginaDevolver() {
  const { token = '' } = useParams()
  const navegar = useNavigate()
  const devolver = useDevolverPorQr()

  const [donde, setDonde] = useState<GeolocationPosition | null>(null)
  const [fallaGps, setFallaGps] = useState<string | null>(null)
  const [novedad, setNovedad] = useState('')
  const [resultado, setResultado] = useState<ResultadoDevolucion | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setFallaGps('Este teléfono no sabe decir dónde está.')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (posicion) => setDonde(posicion),
      (fallo) =>
        setFallaGps(
          fallo.code === fallo.PERMISSION_DENIED
            ? 'Le dijiste que no a la ubicación. Hay que permitirla en los ajustes del teléfono para poder devolver aquí.'
            : 'No se pudo leer la ubicación. Prueba a salir un momento del techo.',
        ),
      // Se pide la buena: bajo el techo de una bodega la rápida es
      // inservible. Y se acepta una lectura de hasta medio minuto: si el
      // teléfono acaba de ubicarse, no hay por qué hacerlo otra vez.
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 },
    )
  }, [])

  async function entregar(confirmarCambio = false) {
    if (!donde) return
    setError(null)

    try {
      const r = await devolver.mutateAsync({
        token,
        posicion: {
          latitud: donde.coords.latitude,
          longitud: donde.coords.longitude,
          exactitud: donde.coords.accuracy,
        },
        novedad,
        confirmarCambio,
      })
      setResultado(r)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo devolver.')
    }
  }

  // ── Sin señal: quedó en el teléfono ─────────────────────────────────────
  // Se escaneó en la bodega y la ubicación de ESTE momento viaja con ella;
  // la base la mide igual al subir. No se da por recibida hasta entonces.
  if (resultado?.estado === 'en_cola') {
    return (
      <div className={estilos.pagina}>
        <Aviso tipo="alerta" titulo="Guardada en el teléfono">
          {resultado.mensaje} Mientras tanto la herramienta sigue a tu nombre. Si al subir la base
          no la acepta, te sale el motivo en «Mi trabajo».
        </Aviso>
        <Boton
          variante="secundario"
          completo
          tamano="grande"
          onClick={() => navegar('/mi-trabajo')}
        >
          Volver a mi trabajo
        </Boton>
      </div>
    )
  }

  // ── Ya está ─────────────────────────────────────────────────────────────
  if (resultado?.estado === 'devuelta') {
    return (
      <div className={estilos.pagina}>
        <div className={estilos.hecho}>
          <p className={estilos.hechoTitulo}>Recibida</p>
          <p className={estilos.hechoQue}>{resultado.herramienta}</p>
          <p className={estilos.hechoEtiqueta}>{resultado.etiqueta}</p>
        </div>

        <p className={estilos.pista}>Apunta la cámara a la siguiente, o vuelve a tu trabajo.</p>

        <Boton
          variante="secundario"
          completo
          tamano="grande"
          onClick={() => navegar('/mi-trabajo')}
        >
          Volver a mi trabajo
        </Boton>
      </div>
    )
  }

  return (
    <div className={estilos.pagina}>
      {/* ── El sistema no sabe cuál es y pregunta ─────────────────────────
          No es un error: es que la unidad escaneada no es la que estaba
          apartada, y «me llevé otra del estante» y «escaneé dos veces» dejan
          la misma huella. Quien está aquí sí lo sabe. */}
      {resultado?.estado === 'confirmar' ? (
        <>
          <Aviso tipo="alerta" titulo="Un momento">
            {resultado.mensaje}
          </Aviso>

          <div className={estilos.botones}>
            <Boton
              variante="principal"
              completo
              tamano="grande"
              trabajando={devolver.isPending}
              onClick={() => void entregar(true)}
            >
              Sí, es esa
            </Boton>
            <Boton variante="secundario" completo onClick={() => navegar('/mi-trabajo')}>
              No, me equivoqué
            </Boton>
          </div>
        </>
      ) : (
        <>
          <p className={estilos.titulo}>Devolver herramienta</p>

          {error && <Aviso tipo="error">{error}</Aviso>}

          {fallaGps ? (
            <Aviso tipo="alerta" titulo="Hace falta la ubicación">
              {fallaGps} También puedes dejarla en bodega y que la reciban ellos.
            </Aviso>
          ) : !donde ? (
            <p className={estilos.buscando}>Mirando dónde estás…</p>
          ) : (
            <>
              <p className={estilos.listo}>
                Ubicación tomada · precisión de {Math.round(donde.coords.accuracy)} m
              </p>

              <label className={estilos.campo}>
                <span>¿Alguna novedad? (opcional)</span>
                <input
                  type="text"
                  value={novedad}
                  onChange={(e) => setNovedad(e.target.value)}
                  placeholder="Se dobló un peldaño, viene sucia…"
                />
              </label>

              <Boton
                variante="principal"
                completo
                tamano="grande"
                trabajando={devolver.isPending}
                onClick={() => void entregar()}
              >
                Entregar en bodega
              </Boton>
            </>
          )}
        </>
      )}
    </div>
  )
}
